<template>
  <div>
    <PageTitle
      v-if="hasPromoData"
      :title="personalizedWelcome"
      class="m-3"
    />
    <PageTitle
      v-else
      :title="unpersonalizedWelcome"
      class="m-3"
    />
    <AcknowledgeModal
      ref="acknowledgeModal"
      @acknowledged="modalAcknowledged"
    >
      <p>
        We see you are responding to a letter we recently sent you in the mail.
        Before continuing, we want to inform you that there was a clerical error in the letter you received.
        Unfortunately, our system failed to populate the correct APR for which you pre-qualified.
        <b>We do not have any lenders on our platform that offer a 0.00% APR for a refinance.</b>
        We apologize for the miscommunication and understand if you do not want to proceed with a full application,
        which will require us to access your credit report.
      </p>

      <p>
        If you do wish to see what your corrected pre-qualified rate would be &mdash; the APR
        that should have populated in the letter you received &mdash; <b>prior to completing an application,
          please give us a call at <a href="tel:+18664989990">(866) 498-9990</a> and we can check that rate for you.</b>
        Note that, if you choose to apply, this may not be the APR for which you are approved,
        depending on if you meet our criteria.
      </p>
    </AcknowledgeModal>
    <!-- Mobile version of intent buttons -->
    <div class="d-lg-none d-xl-none">
      <div
        v-for="intent in intentLinks"
        :key="intent.value"
        class="row text-center"
      >
        <div class="col">
          <button
            class="btn btn-outline-secondary rg-border-radius font-weight-bold my-2 p-3"
            :class="intent.classAddendum"
            @click="clickedIntent(intent.value)"
          >
            <font-awesome-icon
              v-if="intent.hasOwnProperty('icon')"
              :icon="['fas', intent.icon]"
              class="mr-2 lead-channel-primary-color"
            />
            <svg
              v-if="intent.hasOwnProperty('svg')"
              class="mr-2 lead-channel-primary-color svg-inline--fa fa-search fa-w-16"
              xmlns="http://www.w3.org/2000/svg"
              :viewBox="intent.svg.viewBox"
              fill="#98c13d"
            >
              <path :d="intent.svg.path" />
            </svg>
            {{ intent.text }}
          </button>
        </div>
      </div>
    </div>

    <!-- Desktop version of intent buttons -->
    <div class="d-none d-lg-block">
      <div class="row mt-4">
        <div
          v-for="intent in intentLinks"
          :key="intent.value"
          class="col"
          :class="intent.classAddendum"
        >
          <div
            class="card shadow text-center"
            @click="clickedIntent(intent.value)"
          >
            <div class="card-img-top">
              <font-awesome-icon
                v-if="intent.hasOwnProperty('icon')"
                :icon="['fas', intent.icon]"
                class="mt-3 fa-2x lead-channel-primary-color"
              />
              <svg
                v-if="intent.hasOwnProperty('svg')"
                class="mt-3 fa-2x lead-channel-primary-color svg-inline--fa fa-w-20"
                xmlns="http://www.w3.org/2000/svg"
                :viewBox="intent.svg.viewBox"
                fill="#98c13d"
              >
                <path :d="intent.svg.path" />
              </svg>
            </div>

            <div class="card-body">
              <h6>
                <b>{{ intent.text }}</b>
              </h6>
            </div>
          </div>
        </div>
      </div>
    </div>

    <button
      v-if="testing === true"
      @click="goToThanks"
    >
      Go To Thanks
    </button>

    <div
      v-if="hasPreviousRouteName"
      class="pt-5 text-center"
    >
      <InformationSecure class="font-weight-light" />

      <NavigationButton
        color="btn-outline-primary"
        text="Back"
        @click="$router.push({ name: previousRouteName })"
      />
    </div>
  </div>
</template>

<script>
  import { mapActions, mapGetters } from 'vuex';

  import PageTitle from '../common/PageTitle';
  import { customerIntent } from '../../values/application';
  import AcknowledgeModal from '../common/AcknowledgeModal';
  import NavigationButton from '../common/buttons/NavigationButton';
  import InformationSecure from '../common/InformationSecure';

  export default {
    components: {
      PageTitle,
      AcknowledgeModal,
      NavigationButton,
      InformationSecure
    },
    data () {
      return {
        testing: false,
        intentLinks: [
          {
            icon: 'money-bill-wave',
            text: 'Lower my monthly payment',
            classAddendum: 'intentLowerPayment',
            value: customerIntent.LOWER_PAYMENT
          },
          {
            icon: 'percent',
            text: 'Lower my interest rate',
            classAddendum: 'intentLowerRate',
            value: customerIntent.LOWER_RATE
          },
          {
            icon: 'random',
            text: 'Take someone off the loan',
            classAddendum: 'intentTitleRemove',
            value: customerIntent.TITLE_REMOVE
          },
          {
            icon: 'search',
            text: 'Just checking my options',
            classAddendum: 'intentShoppingRates',
            value: customerIntent.SHOP_RATES
          },
          {
            text: 'I want a cash-out refinance',
            classAddendum: 'intentCashOutRefi',
            value: customerIntent.CASH_OUT,
            svg: {
              viewBox: '0 0 144.13 149.1',
              path: `M87.66 0l17.22 23.08-3.84 1.63-43.21 16.67a32.1 32.1 0 0 1-13.43
              2.49c-5.32-.22-10.66 0-16.32-.81l3.36-2.55q26.39-19 52.77-38.1A34 34 0
              0 0 86.91 0zm48.43 82.7c-4.66 0-8.65-.19-12.62 0-9.62.57-16.71 7.38-17.65
              16.72a18.13 18.13 0 0 0 13.88 19.21 39.56 39.56 0 0 0 7.8.56c2.72.08
              5.44 0 8.54 0l-.07 24.94c-.07 2.84-2.31 4.31-5 4.79a18.79 18.79 0 0
              1-3.36.1H8.5c-6.95 0-8.5-1.58-8.5-8.63q0-44.5 0-89.08C0 38.48 1.71 36.8
              14.43 36.88h14.15c-3 2.84-5.23 4.92-8.87 4.65a45.24 45.24 0 0
              0-8.23.06c-1.17.13-2.22 1.32-3.33 2 1 .78 2 2 3.13 2.24a37.61 37.61
              0 0 0 6.72.26h109.38c7.44 0 8.71 1.29 8.71 8.75zm7.91-1.36v24.62a2.91
              2.91 0 0 1-.65 2c-3.91 3.2-7 7.92-13.27 6.78-2.17-.4-4.48 0-6.71-.11A13.65
              13.65 0 0 1 122 87.51a9.34 9.34 0 0 1 1.48-.18c7.33.16 14.82.85 19.7-7zm-13
              19.74a6.87 6.87 0 0 0-6.91-6.86 6.78 6.78 0 0 0-6.82 6.53 7 7 0 0 0 6.68
              7.12 7 7 0 0 0 7.05-6.79zm-16.6-75.99l6.81 18.47h-56l-.21-.67zm9.33
              11.81c4.48 0 8.71-.14 12.93.05a7.9 7.9 0 0 1 7.26 7.31c.16 10 .22 19.94
              0 29.91 0 1.4-2 2.76-3
              4.14l-1.12-.55c-.07-1.16-.2-2.32-.21-3.49V47.72c0-4.85-1.4-6.15-6.3-6.22a39.23
              39.23 0 0 0-4.85 0c-3.16.36-4.7-.89-4.71-4.6zm20.35 76.58c0 7.57.19 15.15-.14
              22.71-.08 1.73-1.95 3.38-3 5.06l-1.34-.62.15-24.44c0-1.15 2.14-2.23 3.29-3.35z`
            }
          }
        ]
      };
    },

    computed: {
      ...mapGetters({
        loadingPercent: 'global/loadingPercent',
        hasPreviousRouteName: 'navigation/hasPreviousRouteName',
        hasPromoData: 'promoCode/hasPromoData',
        previousRouteName: 'navigation/previousRouteName',
        promoOffer: 'promoCode/getOfferData'
      }),
      firstName: function () {
        return this.$options.filters.titleCase(
          this.$store.getters['promoCode/getFirstName']
        );
      },
      personalizedWelcome: function () {
        return 'Hi ' + this.firstName + '! ' + this.unpersonalizedWelcome;
      },
      unpersonalizedWelcome: function () {
        return 'What would you like to change about your auto loan?';
      }
    },

    mounted () {
      this.scrollToTop();
      this.setLoadingPercent(this.$di.workflowService.progress);

      const that = this;
      if (this.hasPromoData) {
        window.setTimeout(function () {
          if (that.promoOffer.interest_rate === 0) {
            that.$refs.acknowledgeModal.show();
            window.dataLayer.push({
              event: 'Zero Percent Advisory Shown',
              'Zero Percent Advisory Shown': 'Zero Percent Advisory Shown'
            });
          }
        }, 1);
      }
    },

    methods: {
      ...mapActions({
        setLoadingPercent: 'global/setLoadingPercent',
        setReasonForLoanChange: 'global/setReasonForLoanChange',
        scrollToTop: 'global/scrollToTop'
      }),
      goToThanks () {
        this.$router.push({ name: 'thanks' });
      },
      clickedIntent (reasonForLoanChange) {
        window.dataLayer.push({
          event: 'reasonForLoanChange',
          reasonForLoanChangeSelected: reasonForLoanChange
        });
        this.setReasonForLoanChange(reasonForLoanChange);

        this.$di.workflowService.next();
      },
      modalAcknowledged () {
        this.$refs.acknowledgeModal.close();
      }
    }
  };
</script>

<style lang="scss" scoped>
  button {
    min-width: 17.5em;
    text-align: left;
  }

  .btn-outline-secondary {
    color: #6c757d;
    border-color: #d5dade;
  }

  .btn-outline-secondary:hover,
  .btn-outline-secondary:not(:disabled):not(.disabled):active,
  .btn-outline-secondary:not(:disabled):not(.disabled).active,
  .show > .btn-outline-secondary.dropdown-toggle {
    color: #6c757d;
    background-color: #fff;
    border-color: #fff;
  }

  .card-body h6 b {
    font-size: 1.05rem;
  }

  .intentCashOutRefi {
    display: none; // Use VWO to display the test intents
  }
  @media (min-width: 768px) {
    .card {
      color: #6c757d;
      border-color: #d5dade;
    }
    .card:hover,
    .card:not(:disabled):not(.disabled):active,
    .card:not(:disabled):not(.disabled).active {
      border-color: var(--primary-color);
      box-shadow: 0 0 0 0.2rem var(--primary-color-shadow);
    }
    .card.shadow:hover {
      box-shadow: 0 0.15rem 0.25rem var(--primary-color-shadow)!important;
    }
  }
</style>
